import { apiGet, apiPost } from './client';
import type { AiSkill, OperationResult, PagedItemsResult } from './types';

// Wraps the AiSkillController endpoints. Each call is a thin pass-through;
// no client-side business logic.

export interface SkillSearchParams {
  search?: string;
  category?: string;
  /**
   * When set, returns only skills currently linked (active link rows) to
   * the given agent. Used by the Skills list "Filter by agent" dropdown.
   */
  agentId?: number;
  activeOnly?: boolean;
  pageNo?: number;
  pageSize?: number;
}

export async function searchSkills(
  params: SkillSearchParams = {}
): Promise<PagedItemsResult<AiSkill>> {
  const { search = '', category = '', agentId, activeOnly, pageNo = 1, pageSize = 100 } = params;
  // OperationResult<PagedItemsResult<AiSkill>> wraps the paged list. The inner
  // PagedItemsResult uses .data (NOT .items) for the array — see types/index.ts.
  const wrapped = await apiGet<OperationResult<PagedItemsResult<AiSkill>>>('/api/AiSkill/Search', {
    search,
    category: category || undefined,
    agentId: agentId && agentId > 0 ? agentId : undefined,
    activeOnly: activeOnly ?? undefined,
    pageNo,
    pageSize,
  });
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to load skills');
  }
  return wrapped.data;
}

export async function getSkillById(id: number): Promise<AiSkill> {
  const result = await apiGet<OperationResult<AiSkill>>(`/api/AiSkill/GetById/${id}`);
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || `Skill ${id} not found`);
  }
  return result.data;
}

export async function getSkillByName(name: string): Promise<AiSkill> {
  const result = await apiGet<OperationResult<AiSkill>>(`/api/AiSkill/GetByName/${encodeURIComponent(name)}`);
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || `Skill '${name}' not found`);
  }
  return result.data;
}

// Returns active skills linked to a specific agent. Each row carries the
// transient AlwaysLoad flag from the link table.
export async function getSkillsForAgent(agentId: number): Promise<AiSkill[]> {
  const result = await apiGet<OperationResult<AiSkill[]>>(`/api/AiSkill/ForAgent/${agentId}`);
  if (!result.isSuccess) throw new Error(result.message || 'Failed to load agent skills');
  return result.data ?? [];
}

export async function updateSkill(skill: Partial<AiSkill> & { id: number }): Promise<AiSkill> {
  const result = await apiPost<OperationResult<AiSkill>>('/api/AiSkill/Update', skill);
  if (!result.isSuccess || !result.data) {
    throw new Error(result.message || 'Failed to update skill');
  }
  return result.data;
}

// ── Smart-editor support ────────────────────────────────────────────────────

export interface AgentLinkRow {
  id: number;
  displayName?: string | null;
  canonicalName?: string | null;
  defaultAI?: boolean;
  patientApp?: boolean;
  merchantApp?: boolean;
}

/**
 * Reverse lookup: every active agent that currently links this skill.
 * Used by the editor's "Used by N agents" panel so authors can see who
 * is downstream-affected before they save.
 */
export async function getAgentsForSkill(skillId: number): Promise<AgentLinkRow[]> {
  const wrapped = await apiGet<OperationResult<AgentLinkRow[]>>(
    `/api/AiSkill/AgentsForSkill/${skillId}`,
  );
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load linked agents');
  return wrapped.data ?? [];
}

/**
 * Idempotent agent ↔ skill link. Backed by /api/AiAgentSkill/Link.
 * `sortOrder` defaults to 100 — the linker UI bumps existing rows up
 * if the user later wants priority control.
 */
export async function linkAgentToSkill(
  agentId: number,
  skillId: number,
  opts?: { sortOrder?: number; alwaysLoad?: boolean },
): Promise<void> {
  const wrapped = await apiPost<OperationResult<unknown>>('/api/AiAgentSkill/Link', {
    agentId,
    skillId,
    sortOrder: opts?.sortOrder ?? 100,
    alwaysLoad: opts?.alwaysLoad ?? false,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to link agent to skill');
}

/**
 * Soft-unlink — sets the link row's IsActive=false. Idempotent: returns
 * normally even when there's no active link (no-op).
 */
export async function unlinkAgentFromSkill(agentId: number, skillId: number): Promise<void> {
  const wrapped = await apiPost<OperationResult<unknown>>('/api/AiAgentSkill/Unlink', {
    agentId,
    skillId,
  });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to unlink');
}

/**
 * Outcome of a skill delete attempt. Mirrors the C# RemoveSkillResult.
 * - `Removed`           — skill row gone (and any link rows along with it).
 * - `RefusedHasLinks`   — agents are currently using the skill and the
 *                          caller did not opt in to unlink. The UI should
 *                          show the linkedAgentIds and offer "unlink + delete".
 * - `NotFound`          — no skill with that id exists.
 */
export interface RemoveSkillOutcome {
  status: 'Removed' | 'RefusedHasLinks' | 'NotFound';
  linksRemoved: number;
  linkedAgentIds: number[];
}

/**
 * Delete a skill with controlled handling of agent links.
 *
 * Default (`unlinkFromAgents` = false) is the safe choice: if the skill is
 * linked to any agents, the server REFUSES the delete and returns the list
 * of linked agent ids. The UI is expected to render those, ask the user to
 * confirm, and re-call with `unlinkFromAgents = true` to unlink + delete in
 * a single step.
 *
 * NOTE: This function does NOT throw on `RefusedHasLinks` — callers must
 * inspect `outcome.status`. It DOES throw on transport / parse errors and
 * on `NotFound` (`isSuccess=false` but no actionable payload).
 */
export async function removeSkill(
  skillId: number,
  opts?: { unlinkFromAgents?: boolean },
): Promise<RemoveSkillOutcome> {
  const wrapped = await apiPost<OperationResult<RemoveSkillOutcome>>(
    `/api/AiSkill/Remove/${skillId}`,
    { unlinkFromAgents: opts?.unlinkFromAgents ?? false },
  );
  // Server returns isSuccess=false for both `NotFound` and
  // `RefusedHasLinks`, but only the latter carries a structured payload the
  // UI can act on. Treat NotFound (no payload) as a hard error.
  if (!wrapped.data) {
    throw new Error(wrapped.message || 'Failed to delete skill');
  }
  return wrapped.data;
}

// ── Import from file ────────────────────────────────────────────────────────

export interface ImportSkillResult {
  id: number;
  name: string;
  category?: string;
  sortOrder: number;
  /** "Inserted" | "Updated" | "Unchanged" — what the upsert actually did. */
  status: string;
}

/**
 * Import a skill from a Markdown file (frontmatter + body). The server
 * reuses the same parser the on-disk provisioning tests use, so a file
 * that imports here will also pass the CI clinical-gates check. Idempotent
 * by frontmatter <c>name</c> — re-uploading the same file is a no-op.
 *
 * Throws with the parser's exact message when the file is malformed
 * (missing frontmatter, bad name↔filename match, empty body, etc.) so the
 * caller can show that message inline next to the upload field.
 */
export async function importSkillFromMarkdown(
  content: string,
  fileName?: string,
): Promise<ImportSkillResult> {
  const wrapped = await apiPost<OperationResult<ImportSkillResult>>('/api/AiSkill/Import', {
    content,
    fileName,
  });
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'Failed to import skill');
  }
  return wrapped.data;
}

export interface SkillDraftRequest {
  userRequest: string;
  currentDraft?: {
    name?: string;
    description?: string;
    category?: string;
    instructions?: string;
  };
  /** Optional. When set, the AI gets the agent's name + description + audience + existing skills as context. */
  agentId?: number;
}

export interface SkillDraftResponse {
  name?: string;
  description?: string;
  category?: string;
  instructions?: string;
  /** AI's "why" for this draft. Surfaced in the editor under the suggestion. */
  rationale?: string;
  /** Existing skills the AI judged similar enough to flag. */
  similarSkillNames?: string[];
}

/**
 * Ask the AI authoring co-pilot for a structured skill draft. The server
 * passes the user's freeform request, the existing skill catalog (for
 * duplicate detection / naming convention), and (optionally) a target
 * agent's context (audience, description, existing skills) to the LLM.
 */
export async function draftSkillWithAi(req: SkillDraftRequest): Promise<SkillDraftResponse> {
  const wrapped = await apiPost<OperationResult<SkillDraftResponse>>('/api/AiSkill/Draft', req);
  if (!wrapped.isSuccess || !wrapped.data) {
    throw new Error(wrapped.message || 'AI drafting failed');
  }
  return wrapped.data;
}
